import React from 'react';
import './SavedResponsesList.scss';
import { SavedResponse } from '../../AIEditor';
import { IconTrash, IconPreview, IconLLMPreview, IconCancel } from 'components/Icons';

export interface SavedResponsesListProps {
  responses: SavedResponse[];
  onSelect: (response: SavedResponse) => void;
  onDelete: (id: string) => void;
  onPurgeAll?: () => void;
}

const SavedResponsesList: React.FC<SavedResponsesListProps> = ({ responses, onSelect, onDelete, onPurgeAll }) => {
  if (responses.length === 0) {
    return (
      <div className="saved-responses-list">
        <div className="saved-responses-list__empty">
          <p>No saved responses yet.</p>
          <p>Run prompts or paste responses to save them for comparison.</p>
        </div>
      </div>
    );
  }

  const formatTimestamp = (timestamp: Date) => {
    return new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).format(timestamp);
  };

  const formatExecutionTime = (executionTime?: number) => {
    if (!executionTime) return null;

    if (executionTime < 1000) {
      return `${executionTime}ms`;
    } else if (executionTime < 60000) {
      return `${(executionTime / 1000).toFixed(1)}s`;
    } else {
      const minutes = Math.floor(executionTime / 60000);
      const seconds = Math.floor((executionTime % 60000) / 1000);
      return `${minutes}m ${seconds}s`;
    }
  };

  const getSourceColor = (source?: string) => {
    switch (source) {
      case 'Run':
        return 'var(--ks-contrast-dark)';
      case 'ChatGPT':
        return '#10a37f';
      case 'Claude':
        return '#cc785c';
      case 'Manual':
      default:
        return 'var(--ks-grey)';
    }
  };

  return (
    <div className="saved-responses-list">
      <div className="saved-responses-list__header">
        <div className="saved-responses-list__header-content">
          <div>
            <h4>Saved Responses ({responses.length})</h4>
            <p>Click to load into preview for comparison</p>
          </div>
          {onPurgeAll && responses.length > 0 && (
            <button
              className="saved-responses-list__text-button--purge"
              onClick={onPurgeAll}
              title="Delete all saved responses"
            >
              Purge All
            </button>
          )}
        </div>
      </div>

      <div className="saved-responses-list__items">
        {responses.map(response => (
          <div key={response.id} className="saved-responses-list__item" onClick={() => onSelect(response)}>
            <div className="saved-responses-list__item-header">
              <div className="saved-responses-list__item-title">{response.title}</div>
              <div className="saved-responses-list__item-actions">
                <button
                  className="saved-responses-list__text-button--delete"
                  onClick={e => {
                    e.stopPropagation(); // Prevent triggering the card click
                    onDelete(response.id);
                  }}
                  title="Delete response"
                >
                  Delete
                </button>
              </div>
            </div>

            <div className="saved-responses-list__item-meta">
              <span className="saved-responses-list__source" style={{ color: getSourceColor(response.source) }}>
                {response.source || 'Manual'}
              </span>
              {response.model && <span className="saved-responses-list__model">{response.model}</span>}
              {response.executionTime && (
                <span className="saved-responses-list__execution-time">
                  {formatExecutionTime(response.executionTime)}
                </span>
              )}
              <span className="saved-responses-list__timestamp">{formatTimestamp(response.timestamp)}</span>
            </div>

            <div className="saved-responses-list__item-preview">
              {response.content.substring(0, 150)}
              {response.content.length > 150 && '...'}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default SavedResponsesList;
